/**
 * Theme catalog. Adding a new theme:
 *   1. Add an entry here.
 *   2. Add `:root[data-theme="<id>"]` block in src/index.css with CSS vars.
 */
export type ThemeId = "default" | "christmas" | "songkran" | "valentine" | "halloween" | "newyear" | "cny";

export interface ThemeMeta {
  id: ThemeId;
  name: string;
  emoji: string;
  description: string;
  /** Two swatch colors for preview cards. */
  swatches: [string, string];
}

export const THEMES: ThemeMeta[] = [
  {
    id: "default",
    name: "Mono",
    emoji: "⚫️",
    description: "Brand default — black & white",
    swatches: ["#000000", "#ffffff"],
  },
  {
    id: "christmas",
    name: "Christmas",
    emoji: "🎄",
    description: "Festive red & green",
    swatches: ["#C8102E", "#006847"],
  },
  {
    id: "songkran",
    name: "Songkran",
    emoji: "💦",
    description: "Splash of water blue",
    swatches: ["#0EA5E9", "#FACC15"],
  },
  {
    id: "valentine",
    name: "Valentine",
    emoji: "💖",
    description: "Pink romance",
    swatches: ["#E91E63", "#FFC0CB"],
  },
  {
    id: "halloween",
    name: "Halloween",
    emoji: "🎃",
    description: "Pumpkin, cream & espresso",
    swatches: ["#B94716", "#FFF8EE"],
  },
  {
    id: "newyear",
    name: "New Year",
    emoji: "🥂",
    description: "Gold on midnight",
    swatches: ["#C9A227", "#0A0A0A"],
  },
  {
    id: "cny",
    name: "Chinese New Year",
    emoji: "🧧",
    description: "Red lanterns & gold",
    swatches: ["#D4181C", "#F2C12E"],
  },
];

export const THEME_STORAGE_KEY = "woxa.theme";

export function getStoredTheme(): ThemeId {
  // Approved release default, independent of local preview tools. Ordinary
  // Docker/CI builds need no extra flags; Mono remains an explicit build option.
  // Ignore stale browser preferences outside preview so everyone gets the release.
  if (process.env.REACT_APP_HALLOWEEN_PREVIEW !== "true") {
    return process.env.REACT_APP_DEFAULT_THEME === "default" ? "default" : "halloween";
  }
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "default" || stored === "halloween") return stored;
  } catch { /* storage may be unavailable in private browsing */ }
  return "halloween";
}

export function applyTheme(id: ThemeId) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", id);
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  // notify other tabs / listeners
  window.dispatchEvent(new CustomEvent("woxa:theme-change", { detail: { id } }));
}
